import React, { useEffect } from 'react';
import styled from 'styled-components';
import SelectSmall from '../select-small';
import Icon, { COLORS, ICONS } from '../icon';
import DurationPicker from '../duration-picker';
import InputClearButton from './input-clear-button';
import classNames from 'classnames';
import GrInputDropdown from './gr-input-dropdown';
import { InvitedPresenter } from '../../app-entry/producer/meeting-modal';
import { Chip, FieldOption, FieldType, FieldValue } from '../../types/stylepacks';
import {ChipContext} from '../../../contexts/generated-content/content-chip-context';
import ChipInputFiller from './chip-input-filler';
import ColorPicker from '../../right-panel/settings/color-pickers/color-picker';

const MAX_DURATION = (60 * 20) - 1;

const disabledCss = `
	border: none;
	background-color: #312F38;
	opacity: 1;
	color: #908CA0;
`;

const StyledInput = styled.textarea`
	display: inline-block;
	box-sizing: border-box;
	color: #E5E4EC;

	&.disabled {
		${disabledCss}
	}
`;

export const StyledInputContainer = styled.div`
	&.input-container {
		margin-bottom: 0;
		& > label {
			margin-left: 16px;
			margin-right: 10px;
			color: #BFBBCF;
		}
		&:last-child {
			margin-bottom: 10px;
		}
		& > .color-toggle {
			border-color: #908CA0;
			border-radius: 100px;
			height: 40px;
			width: 120px;
			display: flex;
			flex-direction: row;
			justify-content: space-between;
			& > .color-swatch {
				margin-left: 4px;
				height: 32px;
				width: 32px;
			}
			& > span {
				text-overflow: initial;
			}
			&.disabled {
				${disabledCss}
			}
		}
	}
`;

export const StyledInputText = styled(StyledInputContainer)`
	position: relative;
	&:nth-child(n) {
		&.disabled {
			${disabledCss}
			margin-bottom:0;
		}
	}
	& > label {
		visibility: hidden;
	}
`;

export const StyledInputRow = styled(StyledInputContainer)`
	display: flex;
	flex-direction: row !important;
	margin-top: 3px;
	margin-left: 0;
	justify-content: space-between !important;
	&.input-container {
		position: relative;
		&.style-row {
			margin-top: 15px
		}
		& > label {
			margin-top: 13px;
			margin-left: 0;
			margin-right: 0;
		}
	}
	& > .select-small.title {
		opacity: 1 !important;
		margin-bottom: 0;
		font-weight: 500;

		&.disabled {
			& > span.select-label.dropdown {
				background-color: #312F38;
				border: none;
				opacity: 1;
				color: #908CA0 !important;
			}
		}
	}
  
  	.duration-container {
	  margin-bottom: 12px;
    }
`;

interface GRInputProps {
	onChange: (event: any) => void;
	value: FieldValue | undefined;
	fieldType?: FieldType;
	required?: boolean;
	inputStyle?: string;
	labelStyle?: string;
	autoFocus?: boolean;
	noAutocomplete?: boolean;
	disabled?: boolean;
	friendlyName: string;
	fieldName: string;
	options: FieldOption[] | null;
	user?: InvitedPresenter
	showChipMenu?: boolean
}

export default function GRInput(props: GRInputProps) {
	const {
		onChange,
		value,
		fieldType = 'text',
		required = false,
		inputStyle = '',
		labelStyle = '',
		autoFocus = false,
		noAutocomplete = true,
		disabled = false,
		friendlyName,
		fieldName,
		options,
		showChipMenu = false
	} = props;

	const {chipMap, clearChip} = React.useContext(ChipContext);

	const [isOpen, setIsOpen] = React.useState(false);
	const [localValue, setLocalValue] = React.useState(value);
	const [isFocused, setIsFocused] = React.useState(false);

	const inputRef: React.MutableRefObject<HTMLTextAreaElement | null> = React.useRef(null);

	const parseClick = (e: MouseEvent) => {
		let stayOpen = false;
		e.composedPath().forEach((e) => {
			//@ts-ignore
			const classname: string = typeof e?.className === 'string' ? e.className : '';
			if (
				(classname.includes('styled-input-container') && classname.includes('is-focused'))
				|| classname.includes('gr-input-dropdown-container')
			) {
				stayOpen = true;
			}
		});

		if (!stayOpen) {
			document.removeEventListener('mousedown', parseClick);
			setIsFocused(false);
		}
	}
	
	const handleFocus = () => {
		document.removeEventListener('mousedown', parseClick);
		document.addEventListener('mousedown', parseClick);
		setIsFocused(true);
	}

	useEffect(() => {
		setIsOpen(false);
		return () => document.removeEventListener('mousedown', parseClick);
	}, []);

	useEffect(() => {
		setLocalValue(value);
	}, [value]);

	const inputCallback = (e: {target: {name: string, value: string}}) => {
		setLocalValue(e.target.value);
		onChange(e);
	}

	const isChipped = chipMap.get(fieldName) && chipMap.get(fieldName)?.chip_type !== Chip.custom && showChipMenu;

	const clearInput = () => {
		if (isChipped) {
			clearChip(fieldName)
		}
		if (localValue === '') return;
		setLocalValue('');
		onChange({target:{name: fieldName, value: ''}});
	}

	if (fieldType === 'select' && options) {
		// convert options to consumable format for SelectSmall
		const selectOptions = options.map((option: any) => ({
			label: option.name,
			value: option.value,
			className: option.className,
		}));
		const currentOption = selectOptions.find((option: any) => option.value === localValue);

		// eslint-disable-next-line @typescript-eslint/no-shadow
		const onChangeSelect = (value: any) => {
			setLocalValue(value);
			onChange({target: { name: fieldName, value }});
		};
		if (!currentOption) {
			return null;
		}
		return <StyledInputRow className="option-row input-container style-row">
			<SelectSmall {...props}
				dropdown
				selected={currentOption}
				options={selectOptions}
				label={friendlyName}
				onChange={onChangeSelect}
				showLabel
				disabled={disabled}
			/>
			{disabled && <div className='lock'><Icon name={ICONS.LOCK} color={COLORS.TEXT_GRAY_400} size={16} /></div>}
		</StyledInputRow>
	} if (fieldType === 'color' && (typeof localValue === 'string' || typeof localValue === 'undefined')) {
		const onChangeHex = (event: any) => {
			setLocalValue(event.hex);
			onChange({target: {
				...event.target,
				name: fieldName,
				value: event?.hex ?? event?.target?.value ?? '',
			}});
		};

		return <StyledInputRow key={fieldName} className='input-container style-row'>
			<label className={labelStyle} htmlFor={fieldName}>{friendlyName}</label>
			<div className={`color-toggle ${disabled ? 'disabled' : ''}`} onClick={() => setIsOpen((prev) => !prev)}>
				<div className="color-swatch" style={{ backgroundColor: localValue }}></div>
				<span className='format-hex'>{localValue}</span>
			</div>
			<ColorPicker
				open={isOpen}
				openHexAtStart={localValue ?? '#000000'}
				onClose={() => setIsOpen(false)}
				onChange={(c) => onChangeHex(c.hex)}
			/>
		</StyledInputRow>
	} if (fieldType === 'timer' && (typeof localValue === 'string' || typeof localValue === 'number')) {
		return <StyledInputRow key={fieldName} className='input-container style-row duration-input-container flex-center'>
			<label className={labelStyle} htmlFor={fieldName} style={{marginTop: 0}}>{friendlyName}</label>
			<DurationPicker
				initialDuration={Number(localValue ?? 60 * 5)}
				maxDuration={MAX_DURATION}
				getDurationCB={(e: any) => {
					const newValue = Number(e);
					setLocalValue(newValue.toString());
					onChange({
						target: {
							name: fieldName,
							value: newValue?.toString() ?? '',
						},
					});
				}}
			/>
		</StyledInputRow>
	}

	return (<>
        <StyledInputText
			key={fieldName}
			className={classNames(
				`input-container styled-input-container`,
				{disabled: disabled, 'is-focused': isFocused}
			)}
			onClick={() => {
				if (inputRef.current) inputRef.current.focus();
			}}
		>
			{isChipped ? <ChipInputFiller dataKey={fieldName}/> : null}
			<StyledInput
				id={fieldName}
				name={fieldName}
				autoFocus={autoFocus}
				autoComplete={noAutocomplete ? 'off' : 'on'}
				disabled={disabled}
				className={classNames(
					"input stylepacks-input-base stylepacks-field-label",
					inputStyle,
					{disabled, isChipped}
				)}
				placeholder={isChipped ? '' : 'Empty'}
				value={typeof localValue === 'string' ? localValue : ''}
				onChange={(e) => {if (!isChipped) inputCallback(e)}}
				required={required}
				ref={inputRef}
				onFocus={handleFocus}
			/>
			<InputClearButton callback={clearInput} canShow={!!(localValue || isChipped)} forceShow={isFocused} showLock={disabled}/>
        </StyledInputText>
		<GrInputDropdown expanded={isFocused && showChipMenu} callback={inputCallback} dataKey={fieldName}/>
	</>);
}
